// =============================================================================
// ui/tabs/ForYouTodayCard.tsx — the top of the Pre-Market panel.
// -----------------------------------------------------------------------------
// Two sentences, computed from the whole book, ported from the old client's
// `marketIntelPersonalNote`. It is the most useful thing on the pre-market screen
// and it needs no input from the trader at all.
//
// ⚠ RENDERS NOTHING when there is no finding to make — no empty card, no
// placeholder. The old client returns '' and the section simply is not there. A
// visible "no edge identified yet" would be a verdict on a book that is merely
// small, printed above the plan the user is about to write.
//
// The ONE exception is a failed read, which gets a muted line. Silence there
// would be indistinguishable from "you have no edge", and those are not remotely
// the same message.
// =============================================================================

import { useMemo } from 'react';
import { useAccounts } from '../state/accounts';
import { useFilters } from '../state/filters';
import { usePatterns } from '../state/patterns';
import { useCoachingRows } from '../state/useCoachingRows';
import { selectForYouToday } from '../../selectors/forYouToday';
import type { Account } from '../../selectors/accounts';
import { money } from '../format';

export function ForYouTodayCard() {
  const { phase, rows, error } = useCoachingRows();
  const { accounts } = useAccounts();
  const { accounts: selection } = useFilters();
  const { patterns } = usePatterns();

  // ⚠ Resolves a name or returns null. `null` becomes "your top setup" below,
  // which is what the old client falls back to when the catalogue no longer has
  // the id — NOT the leaderboard's "(deleted pattern)". A sentence reading "your
  // strongest edge is (deleted pattern)" is not a sentence.
  const namePattern = useMemo(() => {
    const byId = new Map((patterns ?? []).map((p) => [String(p.id), p.name]));
    return (id: string) => byId.get(String(id)) ?? null;
  }, [patterns]);

  const found = useMemo(() => {
    if (!rows || !accounts) return null;
    return selectForYouToday({
      trades: rows,
      accounts: accounts as readonly Account[],
      selection,
      namePattern,
    });
  }, [rows, accounts, selection, namePattern]);

  if (phase === 'failed') {
    return (
      <div className="jp-carry" role="status">
        <div className="jp-carry-h">For you today</div>
        <p className="muted">
          Couldn’t read your trade history, so there’s nothing to say here yet — this is a
          read problem, not a finding about your trading. {error}
        </p>
      </div>
    );
  }

  // ⚠ Loading renders nothing rather than a spinner. The card is additive and
  // sits above the plan form; a placeholder that later collapses shifts the whole
  // panel under the user's cursor.
  if (!found) return null;
  if (!found.edge && !found.leak) return null;

  return (
    <div className="jp-carry" data-testid="for-you-today">
      <div className="jp-carry-h">For you today</div>
      {found.edge ? (
        <p>
          Your strongest edge is <b>{found.edge.name || 'your top setup'}</b> —{' '}
          {found.edge.winPct}% over {found.edge.sample} trades (
          {money(found.edge.expectancy, 0)}/trade). Favor it.
        </p>
      ) : null}
      {found.leak ? (
        <p>
          Your costliest leak is <b>{found.leak.tag}</b> — {found.leak.sample} trades,{' '}
          {money(found.leak.totalPnl, 0)}. Watch for it today.
        </p>
      ) : null}
    </div>
  );
}
